import { faHourglassHalf, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import { tipAttrs } from "../../tooltip";
import { weaponInstanceKey, type Combatant, type EncounterState, type HeldAction } from "@lancer-vtt/domain";
import { PREPARABLE_ACTION_IDS, type ActionDescriptor } from "@lancer-vtt/rules";
import { BrutalistRadioGroup } from "../../../routes/home/BrutalistRadioGroup";
import type { ActionDraft } from "../combatInspector";
import { classifySkirmishCard } from "./chooserClassify";
import { WeaponCard } from "./WeaponCard";

/**
 * The RAW template the trigger field carries as its placeholder — core book p.72's own phrasing
 * ("the trigger for your prepared action must be phrased as 'When X, then Y'…"). It is a
 * **placeholder, never a parser**: the engine stores `triggerText` unparsed and a human decides
 * when it fires (`decisions/0069` ruling 1).
 */
const TRIGGER_PLACEHOLDER =
  "When <X>, then <Y> — X is a hostile or allied character's action, reaction or move";

/** PREPARE's greyed reasons (the prepare-time gate, Core pp. 71-72), registered in `rulesCopyRegistry.ts`. */
export const PREPARE_PICK_VERB = "Pick the quick action to ready";
export const PREPARE_NEEDS_WEAPON = "A held Skirmish must name the weapon it will fire";
export const PREPARE_NEEDS_TRIGGER = "Describe the trigger — a hold with no trigger is refused";
const PREPARE_READY_TITLE = "Hold this action until its trigger fires (or it lapses at the start of your next turn)";

/** Why PREPARE is greyed: no verb, a Skirmish with no weapon named, or no trigger. */
export function prepareBlockReason(args: { readied: string; missingWeapon: boolean }): string {
  if (args.readied === "") return PREPARE_PICK_VERB;
  return args.missingWeapon ? PREPARE_NEEDS_WEAPON : PREPARE_NEEDS_TRIGGER;
}

export type PrepareChooserProps = {
  combatant: Combatant;
  state: EncounterState;
  /**
   * The body's own enumerated descriptor list (`enumerateActions`'s output) — the D4 verb filter's
   * ONLY input. Granted rows are harmless here: their ids are composite (`"<itemId>:<index>"`) and
   * never members of `PREPARABLE_ACTION_IDS`, so the filter drops them without a `source` read.
   * Widening to granted capped actions is Phase 3 and is gated on a browser release for them
   * existing (D6) — do not loosen this filter without that half.
   */
  actions: readonly ActionDescriptor[];
  /** The armed draft — guaranteed prepare-mode + `attackerId === combatant.id` by the caller. */
  aim: Extract<ActionDraft, { mode: "prepare" }>;
  setAim: (a: ActionDraft | null) => void;
  /**
   * Commit the assembled hold. The BODY owns the dispatch, not this component (D7): it already
   * holds `state`, `actorId` and the `encounter` controller, and the gesture has to lead with a
   * real `activate_unit` when the room would have folded one — see the bodies' own `prepare`
   * closures and {@link withLeadingActivation}.
   */
  onPrepare: (held: HeldAction) => void;
};

/**
 * The **inline Prepare chooser** — the mech/NPC QUICK SUPPORT-action composer that arms a held
 * action (RAW Core pp. 71–72), expanded in place when the PREPARE row is armed (`aim.mode ===
 * "prepare"`). Structurally {@link StabilizeChooser}'s form-plus-commit-button shape, NOT a
 * board-click shape: everything the engine's `HeldAction` record needs is collected up front, and
 * nothing is sent until the PREPARE button. The hold's eventual TARGET is deliberately not
 * collected — RAW's own "ram *them*" example is trigger-determined and unknowable at prepare time,
 * so the target is supplied later, at RELEASE (`PreparedHoldPanel`'s RELEASE NOW → a board click).
 *
 * ## The three declarations
 *
 * 1. **The readied verb**, as a segmented switcher. **Derived from the bar's own descriptors, never
 *    hand-written** (`projects/prepare-hold-browser-arming.md` D4): `resolvable && cost === "Quick"
 *    && PREPARABLE_ACTION_IDS.has(id)` over the `actions` this body already enumerated. On a mech
 *    that yields Skirmish / Ram / Grapple / Quick Tech; on an NPC the same set its own table makes
 *    live. A hand-written list would rot the moment a table's `status` moved.
 *
 *    The `cost === "Quick"` conjunct is what excludes **Improvised Attack** (D5): it IS in
 *    `PREPARABLE_ACTION_IDS` and the CLI accepts it, but RAW's PREPARE readies "any other **quick**
 *    action" and Improvised Attack is a Full action. Per `decisions/0122` the book rules on what a
 *    rule means, so the composer declines to offer it; **nothing here edits the engine set**, and
 *    the engine-side fix is recorded under the tracker's Follow-ups.
 *
 * 2. **A weapon**, for the two verbs whose hold needs one named. Skirmish renders this mech's real
 *    weapon cards — the SHARED {@link WeaponCard} off {@link classifySkirmishCard}, so the
 *    Ordnance-window and Loading greying matches the prepare-time gate (`checkPreparableHold`) word
 *    for word rather than by a second, drifting copy. Quick Tech takes no card: it is offered as
 *    **Invade only** and the built record pins `weapon: "act_invade"`, the one flavor
 *    `isAttackShapedHeld` accepts (Scan / Lock On / Bolster have no `RequestAttack` shape to
 *    release through, so readying one would be an armable-but-unreleasable hold). Ram and Grapple
 *    carry no weapon at all — their `act_*` weapon is implied by the verb.
 *
 * 3. **The trigger**, as ONE freeform textarea (D2). Not an X/Y pair of inputs: a structured pair
 *    would imply the engine evaluates the trigger, and it does not.
 *
 * **The engine is the wall; this only mirrors it.** PREPARE stays disabled until a verb is picked,
 * Skirmish has a card, and the trimmed trigger is non-empty — the affordance mirror of the CLI's
 * own refusal of a missing `--trigger` and of `checkPreparableHold`'s weapon-required rule. It
 * invents no rule and carries no force flag: the domain reducer (`applyPrepare`) and the room's
 * `preparePreApplyGate` stay the walls, and a refusal surfaces through the existing rejection
 * toast.
 */
export function PrepareChooser(props: PrepareChooserProps) {
  return (
    <div className="skirmish-hud" role="group" aria-label="Prepare">
      <div className="skirmish-hud-head">
        <span className="skirmish-hud-title"><Icon icon={faHourglassHalf} className="icon-lead" />PREPARE</span>
        <button type="button" className="skirmish-hud-x" onClick={() => props.setAim(null)} aria-label="cancel prepare" {...tipAttrs({ label: "Cancel prepare", key: "Esc" })}>
          <Icon icon={faXmark} />
        </button>
      </div>

      <PrepareControls {...props} />
    </div>
  );
}

/**
 * {@link PrepareChooser}'s form without its shell: the verb, the weapon card, the trigger and
 * PREPARE. The command menu's Prepare card renders this same part (`token-command-menu` 3.2).
 */
export function PrepareControls({
  combatant: c,
  state,
  actions,
  aim,
  setAim,
  onPrepare,
}: PrepareChooserProps) {
  const weapons = c.base.weapons ?? [];

  // D4 — the verb list, derived. See the component doc for why each conjunct is here.
  const verbs = actions.filter((d) => d.resolvable && d.cost === "Quick" && PREPARABLE_ACTION_IDS.has(d.id));

  const readied = aim.readiedActionId;
  const needsCard = readied === "skirmish";
  // Quick Tech's weapon is pinned rather than picked — Invade is the only releasable flavor.
  const pinnedWeapon = readied === "quick_tech" ? "act_invade" : undefined;
  const hasCard = aim.weaponId !== "";
  const triggerSet = aim.triggerText.trim() !== "";
  const ready = readied !== "" && (!needsCard || hasCard) && triggerSet;
  // Why PREPARE is greyed, inline under it (a greyed reason stays inline, the command menu's rule).
  const blockReason = ready ? undefined : prepareBlockReason({ readied, missingWeapon: needsCard && !hasCard });

  const commit = () => {
    const weapon = needsCard ? aim.weaponId : pinnedWeapon;
    onPrepare({
      readiedActionId: readied,
      // `mount` is deliberately omitted: no prepare-time or release-time gate reads it, and the
      // CLI's `--mount` is a display breadcrumb only (`turn.ts`'s `HeldAction`).
      ...(weapon !== undefined ? { weapon } : {}),
      triggerText: aim.triggerText.trim(),
    });
  };

  // Picking a verb clears any weapon carried over from a previous pick — a Skirmish card key left
  // on a Ram hold would be silently dropped by `commit` above, but leaving it on the draft would
  // re-select that card if the player switched back, which is a pick they didn't make.
  const pickVerb = (id: string) => setAim({ ...aim, readiedActionId: id, weaponId: "" });

  return (
    <div className="skirmish-hud-body">
      {verbs.length === 0 ? (
        // Honest empty state rather than a disabled form: a unit whose table makes none of the
        // preparable verbs live has nothing to ready, and saying so beats an inert switcher.
        <div className="skirmish-hud-empty">NO PREPARABLE QUICK ACTION — nothing to ready</div>
      ) : (
        <BrutalistRadioGroup<string>
          name={`prepare-verb-${c.id}`}
          label="Ready"
          value={readied}
          onChange={pickVerb}
          options={verbs.map((d) => ({ value: d.id, label: d.name, ariaLabel: d.name }))}
          caption={
            readied === ""
              ? "Pick the quick action to hold."
              : readied === "quick_tech"
                ? "Quick Tech — held as INVADE (the only releasable tech flavor)."
                : (verbs.find((d) => d.id === readied)?.blurb ?? "")
          }
        />
      )}

      {needsCard &&
        (weapons.length === 0 ? (
          // The NPC-with-no-frozen-weapons case (and a weaponless mech): `checkPreparableHold`
          // REQUIRES a named weapon for a Skirmish hold, so an enabled button here would send a
          // command the room refuses with "Skirmish requires naming the weapon". Say it instead.
          <div className="skirmish-hud-empty">NO WEAPONS — a held Skirmish must name one</div>
        ) : (
          <div className="skirmish-hud-weapons">
            {weapons.map((w) => {
              const key = weaponInstanceKey(w);
              const sel = key === aim.weaponId;
              return (
                <WeaponCard
                  key={key}
                  weapon={w}
                  combatant={c}
                  // `isOverwatch: false` is a fact, not a default — a hold is never armed as an
                  // Overwatch reaction, so the `ordnance-overwatch` arm is unreachable here and
                  // the `ordnance-window` one (the turn-virgin window) is the live Ordnance wall.
                  state={classifySkirmishCard({ weapon: w, combatant: c, state, isOverwatch: false })}
                  selected={sel}
                  preview={{ attackerId: c.id, mode: "skirmish", weaponId: key, profileId: "", accuracy: 0 }}
                  onToggle={() => setAim({ ...aim, weaponId: sel ? "" : key })}
                />
              );
            })}
          </div>
        ))}

      <label className="skirmish-hud-field">
        <span className="skirmish-hud-field-label">Trigger</span>
        <textarea
          aria-label="prepare trigger"
          className="skirmish-hud-select prepare-hud-trigger"
          rows={2}
          placeholder={TRIGGER_PLACEHOLDER}
          value={aim.triggerText}
          onChange={(e) => setAim({ ...aim, triggerText: e.target.value })}
        />
      </label>

      <div className="skirmish-hud-foot">
        {blockReason && <span className="skirmish-hud-note">{blockReason}</span>}
        <button
          type="button"
          aria-label="fire prepare"
          disabled={!ready}
          title={ready ? PREPARE_READY_TITLE : blockReason}
          onClick={commit}
          className="skirmish-hud-go"
        >
          PREPARE
        </button>
      </div>
    </div>
  );
}
